/**
 * Base API client — the ONLY file in explorer-client that calls fetch().
 *
 * All other code routes through:
 *   • query-api.ts  — reads (does NOT mutate data). May use GET or POST.
 *   • command-api.ts — writes (creates, updates, or deletes data).
 *
 * The split is about DATA MUTATION, not HTTP method. For example:
 *   POST /block/search  → query-api  (reads data, doesn't change anything)
 *   POST /ingest          → command-api (creates new blocks)
 *
 * Why every fetch sets `credentials: "include"`:
 *   The staging API hostnames (staging-api-N.putty.zone) sit behind Cloudflare
 *   Zero Trust Access. After the user logs in, Access drops a `CF_Authorization`
 *   cookie on the API hostname. From localhost (or any other origin), browsers
 *   only attach that cross-origin cookie when `credentials: "include"` is set —
 *   default is "same-origin", which silently strips it and the API redirects to
 *   the Access login page on every call. Production (api.putty.me) has no Access
 *   in front of it, so the option is harmless there. Worker CORS is configured
 *   with `credentials: true` and a specific origin allowlist (no wildcards) to
 *   match — see `production/api/src/index.ts`.
 */
import { addBreadcrumb } from "@sentry/react";
import { ConfiguredLogger } from "@/lib/logger";
import { API_BASE_URL } from "@/viewer-config";
const logger = new ConfiguredLogger({ caller: "API" });
let authTokenProvider;
const authTokenProviderWaiters = [];
/**
 * Wire up the auth token provider (called once at app init from AuthProvider).
 * Both QueryApi and CommandApi use this to get Bearer tokens.
 *
 * Resolves every pending `waitForAuthTokenProvider()` caller so any
 * fetch that was deliberately paused for the provider can proceed
 * with valid headers.
 */
export function setAuthTokenProvider(provider) {
    authTokenProvider = provider;
    while (authTokenProviderWaiters.length > 0) {
        const fn = authTokenProviderWaiters.shift();
        fn?.();
    }
}
/**
 * Resolves once the auth token provider has been wired up by
 * `<ApiSetup>` — i.e. once `useAuth()`'s `getAccessToken` is reachable
 * via the module-level `authTokenProvider` singleton. Callers that
 * fire from module init (e.g. TanStack DB `queryFn`s that mount as
 * soon as the first subscriber attaches) use this to defer their
 * network call until the auth context exists; otherwise the fetch
 * lands without an `Authorization` header and the server returns 401.
 *
 * Synchronous resolve when the provider is already set, so call sites
 * pay no extra microtask once auth has been wired.
 */
export function waitForAuthTokenProvider() {
    if (authTokenProvider !== undefined)
        return Promise.resolve();
    return new Promise((resolve) => {
        authTokenProviderWaiters.push(resolve);
    });
}
export async function getAuthHeaders() {
    if (!authTokenProvider) {
        logger.warn("No auth token provider configured", undefined, { sendToSentry: false });
        return {};
    }
    const token = await authTokenProvider();
    if (!token) {
        logger.warn("No auth token available", undefined, { sendToSentry: false });
        return {};
    }
    return { Authorization: `Bearer ${token}` };
}
/**
 * Authenticated GET request. Returns parsed JSON or throws.
 *
 * @param endpoint  - Path after the version prefix, e.g. "/block/list"
 * @param params    - Query string params (appended as ?key=value)
 * @param options   - Version prefix and extra headers
 */
export async function apiGet(endpoint, params, options) {
    const version = options?.version ?? "v1";
    const url = new URL(`${API_BASE_URL}/${version}${endpoint}`);
    if (params) {
        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined)
                url.searchParams.set(key, String(value));
        }
    }
    const authHeaders = await getAuthHeaders();
    const res = await fetch(url.toString(), {
        method: "GET",
        headers: { ...authHeaders, ...options?.headers },
        credentials: "include",
    });
    addBreadcrumb({ category: "api", message: `GET ${endpoint}`, data: { status: res.status }, level: res.ok ? "info" : "error" });
    if (!res.ok) {
        const text = await res.text().catch(() => "Unknown error");
        throw new Error(`HTTP ${res.status}: ${text}`);
    }
    return (await res.json());
}
/**
 * Authenticated POST request. Returns parsed JSON or throws.
 *
 * @param endpoint - Path after the version prefix, e.g. "/ingest"
 * @param body     - Request body (will be JSON.stringify'd)
 * @param options  - Version prefix, extra headers, raw mode
 */
export async function apiPost(endpoint, body, options) {
    const version = options?.version ?? "v1";
    const url = `${API_BASE_URL}/${version}${endpoint}`;
    const authHeaders = await getAuthHeaders();
    const res = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...authHeaders,
            ...options?.headers,
        },
        body: JSON.stringify(body),
        credentials: "include",
    });
    addBreadcrumb({ category: "api", message: `POST ${endpoint}`, data: { status: res.status }, level: res.ok ? "info" : "error" });
    if (!res.ok) {
        const text = await res.text().catch(() => "Unknown error");
        throw new Error(`HTTP ${res.status}: ${text}`);
    }
    return (await res.json());
}
/**
 * Authenticated PATCH request. Returns parsed JSON or throws.
 *
 * @param endpoint - Path after the version prefix, e.g. "/collection/:id"
 * @param body     - Request body (will be JSON.stringify'd)
 * @param options  - Version prefix, extra headers
 */
export async function apiPatch(endpoint, body, options) {
    const version = options?.version ?? "v1";
    const url = `${API_BASE_URL}/${version}${endpoint}`;
    const authHeaders = await getAuthHeaders();
    const res = await fetch(url, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            ...authHeaders,
            ...options?.headers,
        },
        body: JSON.stringify(body),
        credentials: "include",
    });
    addBreadcrumb({ category: "api", message: `PATCH ${endpoint}`, data: { status: res.status }, level: res.ok ? "info" : "error" });
    if (!res.ok) {
        const text = await res.text().catch(() => "Unknown error");
        throw new Error(`HTTP ${res.status}: ${text}`);
    }
    return (await res.json());
}
/**
 * Authenticated DELETE request. Returns parsed JSON or throws.
 *
 * @param endpoint - Path after the version prefix, e.g. "/collection/:id/member/:accountId"
 * @param options  - Version prefix, extra headers
 */
export async function apiDelete(endpoint, options) {
    const version = options?.version ?? "v1";
    const url = `${API_BASE_URL}/${version}${endpoint}`;
    const authHeaders = await getAuthHeaders();
    const res = await fetch(url, {
        method: "DELETE",
        headers: { ...authHeaders, ...options?.headers },
        credentials: "include",
    });
    addBreadcrumb({ category: "api", message: `DELETE ${endpoint}`, data: { status: res.status }, level: res.ok ? "info" : "error" });
    if (!res.ok) {
        const text = await res.text().catch(() => "Unknown error");
        throw new Error(`HTTP ${res.status}: ${text}`);
    }
    return (await res.json());
}
/**
 * Authenticated POST that returns the raw Response (for streaming / NDJSON).
 */
export async function apiPostRaw(endpoint, body, options) {
    const version = options?.version ?? "v1";
    const url = `${API_BASE_URL}/${version}${endpoint}`;
    const authHeaders = await getAuthHeaders();
    const res = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...authHeaders,
            ...options?.headers,
        },
        body: JSON.stringify(body),
        credentials: "include",
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "Unknown error");
        throw new Error(`HTTP ${res.status}: ${text}`);
    }
    return res;
}
// ---------------------------------------------------------------------------
// Multipart POST (for file uploads)
// ---------------------------------------------------------------------------
/**
 * Authenticated POST with FormData body (multipart/form-data).
 * Do NOT set Content-Type — the browser sets it with the boundary.
 */
// eslint-disable-next-line putty/no-direct-fetch
export async function apiPostFormData(endpoint, formData, options) {
    const version = options?.version ?? "v1";
    const url = `${API_BASE_URL}/${version}${endpoint}`;
    const authHeaders = await getAuthHeaders();
    const res = await fetch(url, {
        method: "POST",
        headers: { ...authHeaders, ...options?.headers },
        body: formData,
        credentials: "include",
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "Unknown error");
        throw new Error(`HTTP ${res.status}: ${text}`);
    }
    return (await res.json());
}
